import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_CYAN } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/sky-high.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/sky-high.mp3',
  attribution: '"Sky High" by Elektronomia [NCS Release] - https://ncs.io/SkyHigh',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('sky-high', variant),
    slug: buildVariantSlug('sky-high', variant),
    title: buildVariantTitle('Sky High', variant),
    description:
      'Beginner-friendly. 128 BPM progressive house. Builds into an anthemic four-on-the-floor drop.',
    bpm: onsets.bpm,
    difficulty: 2,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_CYAN,
    audio,
  });

  // 60s @ 128 BPM ≈ 128 beats. Build → drop → break → final drop.
  builder.section({ startBeat: 0, endBeat: 16, kind: 'verse' });
  builder.section({ startBeat: 16, endBeat: 64, kind: 'drop' });
  builder.section({ startBeat: 64, endBeat: 80, kind: 'bridge' });
  builder.section({ startBeat: 80, endBeat: 128, kind: 'drop' });

  // Build: every other strong onset, gives the player room to settle in.
  const buildPads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 16,
    kinds: ['kick', 'snare'],
    minIntensity: 0.3,
    everyNth: 2,
    minSpacing: 1,
  });

  // Drop: every kick + every other snare. Four-on-the-floor groove with
  // off-beat accents.
  const dropPads = mergeBeats(
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['kick'],
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['snare'],
      minIntensity: 0.4,
      everyNth: 2,
      minSpacing: 0.5,
    }),
  );

  // Break: lead melody only. Ear candy without overwhelming the player.
  const breakPads = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 80,
    kinds: ['lead'],
    minIntensity: 0.3,
    minSpacing: 1,
  });

  // Final drop: dense, all kinds, tight spacing for the climax.
  const finalPads = variantSelectBeats(variant, onsets, {
    fromBeat: 80,
    untilBeat: 126,
    kinds: ['kick', 'snare'],
    minIntensity: 0.3,
    minSpacing: 0.5,
  });

  const padBeats = mergeBeats(buildPads, dropPads, breakPads, finalPads);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // Riser tail before the drop and the bridge breakdown both leave long
  // pad-free stretches; let the player mash through them.
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 126,
    minGapBeats: 6,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(128);
  return builder.build();
}

export const SKY_HIGH_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const SKY_HIGH_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(SKY_HIGH_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
